import type { MetaConversation } from 'lib-shared-legacy-types';
import type { ReadConversationApiResponse } from 'lib-shared-types-api';

import type { PhoneNumber } from 'apis/numbers';
import type { AppState } from 'bootstrap.store';

import { twilioTokenRegions } from './types';
import type { TwilioTokenRegion } from './types';

/**
 * Normalises a csid that may arrive as a string or number into a number,
 * returning NaN for invalid input.
 */
export function parseCsid(csid: string | number): number {
  return typeof csid === 'string' ? parseInt(csid, 10) : csid;
}

/**
 * Type guard that checks whether an arbitrary value is a valid
 * TwilioTokenRegion.
 */
export function isTwilioTokenRegion(
  value: unknown,
): value is TwilioTokenRegion {
  return twilioTokenRegions.includes(value as TwilioTokenRegion);
}

/** Returns the owned phone numbers from Redux state. */
function collectOwnedNumbers(state: AppState): PhoneNumber[] {
  return state.telephony.staticTelephoneNumbersArray;
}

/**
 * Returns the PBX region for the phone number that matches the given E.164
 * string, or undefined if no match is found.
 */
export function findPbxRegionForE164(
  numbers: PhoneNumber[],
  e164: string,
): TwilioTokenRegion | undefined {
  return numbers.find(({ e164_format }) => e164_format === e164)?.pbx_region;
}

/**
 * Looks up a MetaConversation by conversation ID, checking both the paginated
 * list and the currently open conversation in Redux.
 */
function findMetaConversationById(
  state: AppState,
  csid: number,
): MetaConversation | undefined {
  const current = state.conversation.metaConversation;

  if (current.conversation?.id === csid) {
    return current;
  }

  const maybeMetaConversation = state.conversations.metaConversations.find(
    (m) => m.conversation.id === csid,
  );

  if (maybeMetaConversation) {
    return maybeMetaConversation;
  }

  return undefined;
}

/**
 * Extracts the `provisioned_number` string from a MetaConversation's
 * conversation attributes.
 */
function readProvisionedNumberFromMetaConversation(
  metaConversation: MetaConversation | undefined,
): string | undefined {
  const attrs = metaConversation?.conversation?.attributes as
    | Record<string, unknown>
    | undefined;
  const provisioned = attrs?.provisioned_number;
  return typeof provisioned === 'string' ? provisioned : undefined;
}

/**
 * Resolves the provisioned PSTN number for a conversation ID by reading the
 * MetaConversation from Redux.
 */
export function getProvisionedNumberForCsid(
  state: AppState,
  csid: string | number,
): string | undefined {
  const id = parseCsid(csid);

  if (Number.isNaN(id)) {
    return undefined;
  }

  const meta = findMetaConversationById(state, id);
  return readProvisionedNumberFromMetaConversation(meta);
}

/**
 * Determines the Twilio token region for an outbound call originating from
 * `fromNumber`. First tries to match `fromNumber` directly against owned
 * numbers; falls back to the agent's currently selected outbound number if no
 * direct match is found.
 */
export function resolveOutboundTokenRegionFromState(
  state: AppState,
  fromNumber: string,
): TwilioTokenRegion | undefined {
  const numbers = collectOwnedNumbers(state);
  const fromRegion = findPbxRegionForE164(numbers, fromNumber);

  if (fromRegion) {
    return fromRegion;
  }

  const selected = state.telephony.selectedFromNumber;

  if (!selected?.e164_format) {
    return undefined;
  }

  return (
    findPbxRegionForE164(numbers, selected.e164_format) ?? selected.pbx_region
  );
}

/**
 * Determines the Twilio token region for an inbound/conversation-driven call by
 * looking up the provisioned number for the given conversation ID and matching
 * it against owned numbers in Redux.
 */
export function resolveConversationLedTokenRegionFromState(
  state: AppState,
  csid: string | number,
): TwilioTokenRegion | undefined {
  const provisioned = getProvisionedNumberForCsid(state, csid);

  if (!provisioned) {
    return undefined;
  }
  return resolveTokenRegionFromProvisionedE164(state, provisioned);
}

/**
 * Maps a provisioned PSTN number to a Twilio token region using owned numbers
 * already in Redux (static + dynamic).
 */
export function resolveTokenRegionFromProvisionedE164(
  state: AppState,
  provisionedE164: string,
): TwilioTokenRegion | undefined {
  return findPbxRegionForE164(collectOwnedNumbers(state), provisionedE164);
}

export function readProvisionedNumberFromGetConversationResponse(
  data: ReadConversationApiResponse['data'],
): string | undefined {
  const attrs = data.history.main_conversation.attributes as
    | Record<string, unknown>
    | undefined;

  if (attrs == null || !('provisioned_number' in attrs)) {
    return undefined;
  }

  const provisioned = attrs.provisioned_number;
  return typeof provisioned === 'string' ? provisioned : undefined;
}
